import React, { useState, useEffect } from "react";
import { X, Loader2, Save } from "lucide-react";
import api, { notifyError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";

const PLANS = ["STARTER", "GROWTH", "ENTERPRISE"];
const TYPES = ["TRIAL", "MONTHLY", "SIX_MONTHS", "YEARLY", "LIFETIME"];

// Shared dark-theme classes untuk konsistensi dengan modal (bg-[#0F0F17])
const SELECT_TRIGGER_CLS =
  "w-full bg-white/5 border-white/10 text-white hover:bg-white/10 focus:ring-emerald-500 data-[placeholder]:text-white/40";
const SELECT_CONTENT_CLS =
  "z-[110] bg-[#0F0F17] border-white/10 text-white shadow-2xl";
const SELECT_ITEM_CLS =
  "text-white focus:bg-white/10 focus:text-white data-[state=checked]:bg-emerald-500/20 data-[state=checked]:text-emerald-300";

/**
 * SubscriptionFormDialog — buat atau edit subscription.
 * Props:
 *   mode: "create" | "edit"
 *   subscription: existing (edit)
 *   tenants: [] (list untuk picker saat create)
 *   open, onClose, onSaved
 */
export default function SubscriptionFormDialog({ mode = "create", subscription, tenants = [], open, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      tenant_id: subscription?.tenant_id || (tenants[0]?.tenant_id || ""),
      plan_code: subscription?.plan_code || "STARTER",
      subscription_type: subscription?.subscription_type || "TRIAL",
      end_date: subscription?.end_date?.slice(0, 10) || "",
      is_auto_renew: subscription?.is_auto_renew || false,
    });
  }, [open, subscription, tenants]);

  if (!open || !form) return null;
  const set = (patch) => setForm({ ...form, ...patch });

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        plan_code: form.plan_code,
        subscription_type: form.subscription_type,
        is_auto_renew: form.is_auto_renew,
      };
      if (form.end_date) payload.end_date = new Date(form.end_date).toISOString();
      let r;
      if (isEdit) {
        r = await api.put(`/platform/subscriptions/${subscription.id}`, payload);
      } else {
        payload.tenant_id = form.tenant_id;
        r = await api.post("/platform/subscriptions", payload);
      }
      toast.success(isEdit ? "Subscription diperbarui." : "Subscription dibuat.");
      onSaved?.(r.data.subscription);
      onClose?.();
    } catch (e) { notifyError(e, "Gagal menyimpan subscription"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm grid place-items-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose?.()} data-testid="subscription-form">
      <div className="w-full max-w-lg bg-[#0F0F17] border border-white/10 rounded-3xl">
        <div className="px-6 py-4 border-b border-white/10 flex items-center gap-3">
          <div className="text-white font-display font-black text-lg">
            {isEdit ? "Edit Subscription" : "Buat Subscription"}
          </div>
          <button onClick={onClose} className="ml-auto w-8 h-8 rounded-lg grid place-items-center text-white/60 hover:text-white hover:bg-white/10">
            <X size={16} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          {!isEdit && (
            <div>
              <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5">Tenant *</label>
              <Select value={form.tenant_id} onValueChange={(v) => set({ tenant_id: v })}>
                <SelectTrigger className={SELECT_TRIGGER_CLS} data-testid="sub-form-tenant">
                  <SelectValue placeholder="Pilih tenant…" />
                </SelectTrigger>
                <SelectContent className={SELECT_CONTENT_CLS}>
                  {tenants.map((t) => (
                    <SelectItem
                      key={t.tenant_id}
                      value={t.tenant_id}
                      className={SELECT_ITEM_CLS}
                      data-testid={`sub-tenant-${t.tenant_code}`}
                    >
                      {t.tenant_name} · {t.tenant_code}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5">Plan Code</label>
              <div className="flex flex-wrap gap-1.5">
                {PLANS.map((p) => (
                  <button key={p} type="button" onClick={() => set({ plan_code: p })}
                    className={`px-2.5 py-1 rounded-full border text-[10px] font-black uppercase tracking-wider ${
                      form.plan_code === p ? "bg-white text-black border-white" : "border-white/10 text-white/60 hover:text-white"
                    }`} data-testid={`sub-plan-${p.toLowerCase()}`}>{p}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5">Type</label>
              <Select value={form.subscription_type} onValueChange={(v) => set({ subscription_type: v })}>
                <SelectTrigger className={SELECT_TRIGGER_CLS} data-testid="sub-form-type">
                  <SelectValue placeholder="Pilih tipe…" />
                </SelectTrigger>
                <SelectContent className={SELECT_CONTENT_CLS}>
                  {TYPES.map((t) => (
                    <SelectItem
                      key={t}
                      value={t}
                      className={SELECT_ITEM_CLS}
                      data-testid={`sub-type-${t.toLowerCase()}`}
                    >
                      {t.replace("_", " ")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5">End Date (opsional — otomatis dari type)</label>
            <Input type="date" value={form.end_date} onChange={(e) => set({ end_date: e.target.value })}
              className="bg-white/5 border-white/10 text-white" data-testid="sub-form-end-date" />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.is_auto_renew} onChange={(e) => set({ is_auto_renew: e.target.checked })}
              className="w-4 h-4 accent-emerald-500" data-testid="sub-form-autorenew" />
            <span className="text-xs text-white/80">Auto Renew</span>
          </label>
        </div>
        <div className="px-6 py-4 border-t border-white/10 flex items-center gap-2 justify-end">
          <Button variant="ghost" onClick={onClose} className="text-white/60 hover:text-white hover:bg-white/10">Batal</Button>
          <Button onClick={submit} disabled={busy || !form.tenant_id} className="bg-emerald-500 text-white hover:bg-emerald-600" data-testid="sub-form-submit">
            {busy ? <><Loader2 size={14} className="mr-1 animate-spin" /> Menyimpan…</> : <><Save size={14} className="mr-1" /> {isEdit ? "Simpan" : "Buat"}</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
